import type { SceneConfig, SceneTransform } from './sceneConfig';
import type { SightId } from '../types';

const SCENE_SPZ_URL = new URL('../assets/models/scene.spz', import.meta.url).href;
const KAABA_SPLAT_URL = new URL('../assets/models/kaaba.sog', import.meta.url).href;
const KAABA_DETAILED_SPLAT_URL = new URL('../assets/models/Kaaba_1500k_new_gate_sky_buildings.sog', import.meta.url).href;
const KAABA_DETAILED_WITHOUT_SKY_SPLAT_URL = new URL('../assets/models/Kaaba_1500k_new_gate_buildings.sog_v2.sog', import.meta.url).href;
const KAABA_DETAILED_WITHOUT_BUILDING_SPLAT_URL = new URL('../assets/models/Kaaba_1500k_new_gate_new.sog', import.meta.url).href;
const KAABA_STABLE_SPLAT_URL = new URL('../assets/models/Kaaba_1500k_new_gate_stable.sog', import.meta.url).href;
const ALHARAM_SPLAT_URL = new URL(
  '../assets/models/alharam_setuped.sog',
  import.meta.url
).href;

const CUBE_MODEL_URL = new URL('../assets/models/S_Cube.fbx', import.meta.url).href;
const SQUARE_COLLISION_MODEL_URL = new URL(
  '../assets/models/S_SquareCollision.fbx',
  import.meta.url
).href;

const PREVIEW_MODEL_TRANSFORM: SceneTransform = {
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 0.02, y: 0.02, z: 0.02 }
};

const DEFAULT_VIRTUAL_TOUR_SIGHT_IDS: SightId[] = [
  // 'kaaba',
  'black-stone',
  'kiswah',
  'door',
  'golden-spout',
  'hijr-ismail',
  'maqam-ibrahim',
  // 'grand-mosque',
];

export const kaabaSceneConfig: SceneConfig = {
  id: 'kaaba',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 65,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: 0, y: 0, z: 1 },
  },
  splat: {
    // Desktop/mobile variants can diverge later; now both use the same file.
    defaultByQuality: {
      desktop: 'kaaba_detailed_without_sky',
      mobile: 'kaaba_detailed_without_sky',
    },
    variants: [
      {
        key: 'scene',
        url: SCENE_SPZ_URL,
        aliases: ['scene.spz', '/scene.spz'],
      },
      {
        key: 'kaaba',
        url: KAABA_SPLAT_URL,
        aliases: ['kaaba.sog', '/kaaba.sog'],
      },
      {
        key: 'kaaba_detailed',
        url: KAABA_DETAILED_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_detailed_without_sky',
        url: KAABA_DETAILED_WITHOUT_SKY_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'kaaba_detailed_without_building',
        url: KAABA_DETAILED_WITHOUT_BUILDING_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
        skybox: {
          url: '/skybox_with_buildings_light.jpg',
          mobileUrl: '/skybox_with_buildings_light.jpg',
          intensity: 1.0,
          mobileIntensity: 1.0,
        },
      },
      {
        key: 'kaaba_stable',
        url: KAABA_STABLE_SPLAT_URL,
        transform: {
          position: { x: -0.008, y: 0, z: 0.008 },
          rotation: { x: 0, y: 0, z: Math.PI },
          scale: { x: 0.02, y: 0.02, z: 0.02 }
        },
        overrideDefaultTransform: true,
      },
      {
        key: 'alharam_setuped',
        url: ALHARAM_SPLAT_URL,
        aliases: ['alharam_setuped.sog', '/alharam_setuped.sog'],
        // transform: PREVIEW_MODEL_TRANSFORM,
        // overrideDefaultTransform: true,
      },
    ],
    defaultTransform: {
      rotation: { x: 0, y: 0, z: Math.PI },
    },
    revealAnimationDuration: 3,
    revealDistanceScale: 2.5,
  },
  previewModel: {
    url: '/kaaba_preview_1mb_meshopt.glb',
    transform: PREVIEW_MODEL_TRANSFORM,
    style: {
      wavesCenter: { x: -0.32, y: 0, z: 0.48 },
      wavesMinDistance: 0.45,
      wavesAmplitude: 1.8,
      wavesPeriod: 1.0,
      wavesSpeed: 1.5,
      wavesColorIntensity: 5.0,
      wavesDisplacement: 0.03,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a2027,
      intensity: 0.45,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.85,
        position: { x: -0.8, y: 1.2, z: 0.6 },
      },
      {
        color: 0xbdd4ff,
        intensity: 0.35,
        position: { x: 0.9, y: 0.6, z: -0.45 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: '/skybox.jpg',
        mobileUrl: '/skybox.jpg',
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 0, 0]
      }
    },
    collisionMeshes: [
      // {
      //   url: COLLISION,
      //   name: 'collision',
      //   addToCollisionSystem: true,
      //   visible: true,
      //   hideGeometry: false,
      //   showEdgesInDev: true,
      //   edgeColor: 0x00ff00,
      //   // scale: 0.002,
      //   // position: { x: -0.3, y: 0.15, z: 0.5 },
      //   doubleSided: true
      // },
      {
        url: CUBE_MODEL_URL,
        name: 'S_Cube',
        addToCollisionSystem: true,
        visible: false,
        hideGeometry: true,
        showEdgesInDev: true,
        edgeColor: 0x00ff00,
        scale: 0.002,
        position: { x: -0.3, y: 0.15, z: 0.5 },
      },
      {
        url: SQUARE_COLLISION_MODEL_URL,
        name: 'S_SquareCollision',
        addToCollisionSystem: true,
        visible: false,
        hideGeometry: true,
        showEdgesInDev: true,
        edgeColor: 0x0000ff,
        scale: 0.008,
        position: { x: 0, y: 0.3, z: 0 },
        rotation: { x: 0, y: Math.PI, z: 0 },
        doubleSided: true,
      },
    ],
    collisionVolume: {
      inflateMultiplier: 1.1,
      fallbackSize: { x: 10, y: 10, z: 10 },
    },
  },
  virtualTour: DEFAULT_VIRTUAL_TOUR_SIGHT_IDS.map((id) => ({
    blocks: [],
    suggestions: [],
    actions: [{ type: 'focus-sight', sightId: id }],
  })),
};
